import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Dimensions,
  ScrollView,
  NativeSyntheticEvent,
  NativeScrollEvent,
  LayoutChangeEvent,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { AppIcon } from './icons/AppIcon';
import * as Clipboard from 'expo-clipboard';
import { colors } from '../theme/colors';
import { spacing } from '../theme/spacing';
import { typography } from '../theme/typography';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useTypography } from '../hooks/useTypography';
import { useAppContext } from '../contexts/AppContext';
import { WhatsAppPhoneCardData, QUALITY_COLORS } from './whatsapp/WhatsAppPhoneCard';
import { FacebookPage, XTwitterAccount, PhoneAccount } from '../hooks/useProfileCompletion';
import { StatChip } from './StatChip';
import { useThemedStyles } from '../hooks/useThemedStyles';

const { width: screenWidth } = Dimensions.get('window');

// Detect if we're on mobile browser (not just web platform)
const isMobileBrowser = Platform.OS === 'web' && (screenWidth < 768);

// Responsive card sizing
const getCardWidth = (type: 'platform' | 'action' | 'campaign') => {
  if (isMobileBrowser) {
    // Mobile browser gets mobile app layout with better sizing
    switch (type) {
      case 'platform': return screenWidth * 0.4;
      case 'action': return Math.max(90, (screenWidth - spacing.screenPadding * 2 - spacing.sm * 7) / 8);
      case 'campaign': return screenWidth * 0.7;
      default: return screenWidth * 0.4;
    }
  } else if (Platform.OS === 'web') {
    // Desktop web gets optimized layout
    switch (type) {
      case 'platform': return Math.min(320, screenWidth * 0.22);
      case 'action': return Math.min(140, screenWidth * 0.08);
      case 'campaign': return Math.min(400, screenWidth * 0.28);
      default: return 320;
    }
  } else {
    // Native mobile app - use larger width for better mobile experience
    switch (type) {
      case 'platform': return screenWidth * 0.4;
      case 'action': return Math.max(80, (screenWidth - spacing.screenPadding * 2 - spacing.sm * 7) / 8);
      case 'campaign': return screenWidth * 0.7;
      default: return screenWidth * 0.4;
    }
  }
};

export interface ServiceStatus {
  name: string;
  code: string;
  status: 'completed' | 'incomplete' | 'not-started';
  icon: string;
  color: any; // Accept both string and ColorValue
  description: string;
}

export interface ProfileCompletionData {
  percentage: number;
  totalServices: number;
  completedServices: number;
  services: ServiceStatus[];
}

interface ProfileCompletionWidgetProps {
  data: ProfileCompletionData;
  onCompleteProfile?: () => void;
  onCompleteService?: (service: ServiceStatus) => void;
  showIncompleteServices?: boolean;
  onRefresh?: () => void;
  /** Whether the current user can configure services (admin/owner only) —
   * when false, service cards render status/detail read-only, without the
   * settings/complete action buttons. The widget itself stays visible to
   * all roles regardless of this flag. */
  canConfigure?: boolean;
  /** Connected WhatsApp phone numbers — rendered as an extra detail row on
   * the WhatsApp service card (count + worst-case quality indicator). */
  whatsappPhones?: WhatsAppPhoneCardData[];
  /** Connected Facebook pages — mirrors the WhatsApp detail row (count +
   * connected/not status dot) on the Facebook Messenger service card. */
  facebookPages?: FacebookPage[];
  /** Connected X (Twitter) accounts — same pattern on the X service card. */
  xAccounts?: XTwitterAccount[];
  /** Connected phone channel account (single account, whichever provider is
   * configured e.g. InnoCalls) — same detail-row pattern on the Phone card. */
  phoneAccount?: PhoneAccount | null;
  /** Business-wide stats (contacts, messages this month, team size) —
   * rendered as a persistent strip, not tied to the per-service carousel. */
  businessStats?: { totalContacts: number; messagesThisMonth: number; teamCount: number } | null;
}

export const ProfileCompletionWidget: React.FC<ProfileCompletionWidgetProps> = ({
  data,
  onCompleteProfile,
  onCompleteService,
  showIncompleteServices = true,
  onRefresh,
  canConfigure = true,
  whatsappPhones = [],
  facebookPages = [],
  xAccounts = [],
  phoneAccount = null,
  businessStats = null,
}) => {
  const styles = useThemedStyles(getStyles);
  const { t, isRTL } = useLocale();
  const rtl = useRTLStyles();
  const typo = useTypography();
  const { showNotification } = useAppContext();
  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);
  const [serviceCardWidth, setServiceCardWidth] = useState(0);
  const serviceScrollRef = useRef<ScrollView>(null);
  const isProgrammaticServiceScroll = useRef(false);

  const copyToClipboard = async (value: string) => {
    try {
      if (Platform.OS === 'web' && navigator.clipboard) {
        await navigator.clipboard.writeText(value);
      } else {
        await Clipboard.setStringAsync(value);
      }
      showNotification('success', t('profile.copy.successTitle'), t('profile.copy.successMessage'));
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      showNotification('error', t('profile.copy.errorTitle'), t('profile.copy.errorMessage'));
    }
  };

  const allServices = data.services; // Show all services, not just incomplete ones
  // Clone-padding loop: [last, ...allServices, first] so swiping past either end
  // lands on a clone, then we silently (no animation) jump to the real card on
  // the opposite side — same loop behavior as the arrow buttons.
  const loopServices = allServices.length > 1 ? [allServices[allServices.length - 1], ...allServices, allServices[0]] : allServices;
  const serviceScrollIndexOffset = allServices.length > 1 ? 1 : 0;
  const hasServices = allServices.length > 0;

  // Helper functions for service status
  const getStatusColor = (status: 'completed' | 'incomplete' | 'not-started') => {
    switch (status) {
      case 'completed': return '#10B981';
      case 'incomplete': return '#F59E0B';
      case 'not-started': return '#6B7280';
      default: return '#6B7280';
    }
  };

  const getStatusText = (status: 'completed' | 'incomplete' | 'not-started') => {
    switch (status) {
      case 'completed': return t('profile.completion.configured');
      case 'incomplete': return t('profile.completion.incomplete');
      case 'not-started': return t('profile.completion.notStarted');
      default: return t('profile.completion.unknown');
    }
  };

  // Worst-case quality among all connected numbers, so problems surface
  // without listing every number inline — RED > YELLOW > GREEN > UNKNOWN.
  const QUALITY_PRIORITY = ['RED', 'YELLOW', 'GREEN', 'UNKNOWN'];
  const worstQuality = whatsappPhones.reduce((worst, phone) => {
    const rating = (phone.qualityRating || 'UNKNOWN').toUpperCase();
    return QUALITY_PRIORITY.indexOf(rating) < QUALITY_PRIORITY.indexOf(worst) ? rating : worst;
  }, 'UNKNOWN');

  const renderWhatsAppDetail = (service: ServiceStatus) => {
    if (service.code !== 'wabu') return null;
    return (
      <View style={[styles.whatsappDetailRow, { flexDirection: rtl.rowDirection }]}>
        {whatsappPhones.length === 0 ? (
          <Text style={[styles.whatsappDetailText, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.primary }]}>
            {t('profile.whatsapp.noNumbers')}
          </Text>
        ) : (
          <>
            <View style={[styles.qualityDot, { backgroundColor: QUALITY_COLORS[worstQuality] || QUALITY_COLORS.UNKNOWN }]} />
            <Text style={[styles.whatsappDetailText, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.primary }]} numberOfLines={1}>
              {whatsappPhones.length === 1
                ? t('profile.whatsapp.oneNumber', { phone: whatsappPhones[0].displayPhoneNumber })
                : t('profile.whatsapp.multipleNumbers', { count: whatsappPhones.length })}
            </Text>
            {whatsappPhones.length === 1 && (
              <TouchableOpacity
                onPress={() => copyToClipboard(whatsappPhones[0].displayPhoneNumber)}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="copy-outline" size={14} color={colors.textLight} />
              </TouchableOpacity>
            )}
          </>
        )}
      </View>
    );
  };

  const facebookConnected = facebookPages.some((p) => p.status === 'active' || p.isVerified);
  const renderFacebookDetail = (service: ServiceStatus) => {
    if (service.code !== 'fbm') return null;
    return (
      <View style={[styles.whatsappDetailRow, { flexDirection: rtl.rowDirection }]}>
        {facebookPages.length === 0 ? (
          <Text style={[styles.whatsappDetailText, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.primary }]}>
            {t('profile.facebook.noPagesConnected')}
          </Text>
        ) : (
          <>
            <View style={[styles.qualityDot, { backgroundColor: facebookConnected ? QUALITY_COLORS.GREEN : QUALITY_COLORS.UNKNOWN }]} />
            <Text style={[styles.whatsappDetailText, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.primary }]} numberOfLines={1}>
              {facebookPages.length === 1
                ? t('profile.facebook.onePage', { name: facebookPages[0].page_name })
                : t('profile.facebook.connectedPages', { count: facebookPages.length })}
            </Text>
            {facebookPages.length === 1 && (
              <TouchableOpacity
                onPress={() => copyToClipboard(facebookPages[0].page_name)}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="copy-outline" size={14} color={colors.textLight} />
              </TouchableOpacity>
            )}
          </>
        )}
      </View>
    );
  };

  const xConnected = xAccounts.some((a) => a.status === 'active' && !a.tokenExpired);
  const renderXDetail = (service: ServiceStatus) => {
    if (service.code !== 'x_twitter') return null;
    return (
      <View style={[styles.whatsappDetailRow, { flexDirection: rtl.rowDirection }]}>
        {xAccounts.length === 0 ? (
          <Text style={[styles.whatsappDetailText, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.primary }]}>
            {t('profile.xtwitter.noAccountsConnected')}
          </Text>
        ) : (
          <>
            <View style={[styles.qualityDot, { backgroundColor: xConnected ? QUALITY_COLORS.GREEN : QUALITY_COLORS.UNKNOWN }]} />
            <Text style={[styles.whatsappDetailText, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.primary }]} numberOfLines={1}>
              {xAccounts.length === 1
                ? t('profile.xtwitter.oneAccount', { name: xAccounts[0].displayName })
                : t('profile.xtwitter.connectedAccounts', { count: xAccounts.length })}
            </Text>
            {xAccounts.length === 1 && (
              <TouchableOpacity
                onPress={() => copyToClipboard(xAccounts[0].displayName)}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="copy-outline" size={14} color={colors.textLight} />
              </TouchableOpacity>
            )}
          </>
        )}
      </View>
    );
  };

  const phoneConnected = phoneAccount?.status === 'active';
  const renderPhoneDetail = (service: ServiceStatus) => {
    if (service.code !== 'phone') return null;
    return (
      <View style={[styles.whatsappDetailRow, { flexDirection: rtl.rowDirection }]}>
        {!phoneAccount ? (
          <Text style={[styles.whatsappDetailText, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.primary }]}>
            {t('profile.phone.noAccountConnected')}
          </Text>
        ) : (
          <>
            <View style={[styles.qualityDot, { backgroundColor: phoneConnected ? QUALITY_COLORS.GREEN : QUALITY_COLORS.UNKNOWN }]} />
            <Text style={[styles.whatsappDetailText, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.primary }]} numberOfLines={1}>
              {t('profile.phone.connectedVia', { provider: phoneAccount.provider })}
            </Text>
            {phoneAccount.callerNumber && (
              <TouchableOpacity
                onPress={() => copyToClipboard(phoneAccount.callerNumber!)}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="copy-outline" size={14} color={colors.textLight} />
              </TouchableOpacity>
            )}
          </>
        )}
      </View>
    );
  };

  const getStatusIcon = (status: 'completed' | 'incomplete' | 'not-started') => {
    switch (status) {
      case 'completed': return 'checkmark-circle';
      case 'incomplete': return 'alert-circle-outline';
      case 'not-started': return 'close-circle-outline';
      default: return 'help-circle-outline';
    }
  };

  useEffect(() => {
    if (allServices.length > 1 && serviceCardWidth > 0) {
      serviceScrollRef.current?.scrollTo({ x: (currentServiceIndex + serviceScrollIndexOffset) * serviceCardWidth, animated: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceCardWidth]);

  const scrollToServiceIndex = (index: number) => {
    isProgrammaticServiceScroll.current = true;
    serviceScrollRef.current?.scrollTo({ x: (index + serviceScrollIndexOffset) * serviceCardWidth, animated: true });
    setCurrentServiceIndex(index);
  };

  const goToNextService = () => {
    scrollToServiceIndex((currentServiceIndex + 1) % allServices.length);
  };

  const goToPreviousService = () => {
    scrollToServiceIndex((currentServiceIndex - 1 + allServices.length) % allServices.length);
  };

  const handleServiceLayout = (e: LayoutChangeEvent) => setServiceCardWidth(e.nativeEvent.layout.width);

  const handleServiceScrollSettled = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (isProgrammaticServiceScroll.current) {
      isProgrammaticServiceScroll.current = false;
      return;
    }
    if (!serviceCardWidth) return;
    const loopIndex = Math.round(e.nativeEvent.contentOffset.x / serviceCardWidth);

    if (allServices.length > 1 && loopIndex === 0) {
      // Landed on the cloned last card (swiped left past the first) — snap to the real last card.
      isProgrammaticServiceScroll.current = true;
      serviceScrollRef.current?.scrollTo({ x: allServices.length * serviceCardWidth, animated: false });
      setCurrentServiceIndex(allServices.length - 1);
      return;
    }
    if (allServices.length > 1 && loopIndex === allServices.length + 1) {
      // Landed on the cloned first card (swiped right past the last) — snap to the real first card.
      isProgrammaticServiceScroll.current = true;
      serviceScrollRef.current?.scrollTo({ x: 1 * serviceCardWidth, animated: false });
      setCurrentServiceIndex(0);
      return;
    }
    const index = loopIndex - serviceScrollIndexOffset;
    setCurrentServiceIndex(Math.max(0, Math.min(index, allServices.length - 1)));
  };

  const renderServiceCard = (service: ServiceStatus) => (
    <View style={styles.serviceCard}>
      <View style={[styles.serviceHeader, { flexDirection: rtl.rowDirection }]}>
        <View style={[styles.serviceHeaderLeft, { flexDirection: rtl.rowDirection }]}>
          <AppIcon name={service.icon} size={20} color={service.color} />
          <Text
            style={[styles.serviceName, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.semibold }]}
            numberOfLines={1}
          >
            {service.name}
          </Text>
          <View style={[styles.statusDot, { backgroundColor: getStatusColor(service.status) }]} />
        </View>
        <View style={[styles.serviceHeaderRight, { flexDirection: rtl.rowDirection }]}>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(service.status) + '20' }]}>
            <Text style={[styles.statusText, { color: getStatusColor(service.status), fontFamily: typo.fontFamily.medium }]}>
              {getStatusText(service.status)}
            </Text>
          </View>
          {service.status === 'completed' ? (
            canConfigure && (
              <TouchableOpacity
                style={styles.settingsButton}
                onPress={() => onCompleteService?.(service)}
                activeOpacity={0.7}
                accessibilityLabel={t('profile.completion.configureService', { name: service.name })}
              >
                <Ionicons name="settings-outline" size={18} color={colors.primary} />
              </TouchableOpacity>
            )
          ) : canConfigure ? (
            <TouchableOpacity
              style={[styles.completeServiceIconButton, { backgroundColor: getStatusColor(service.status) }]}
              onPress={() => onCompleteService?.(service)}
              activeOpacity={0.7}
              accessibilityLabel={t('profile.completion.completeService', { name: service.name })}
            >
              <Ionicons name="add" size={18} color={colors.white} />
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
      <Text
        style={[styles.serviceDescription, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.primary }]}
        numberOfLines={1}
        ellipsizeMode="tail"
      >
        {service.description}
      </Text>
      {renderWhatsAppDetail(service)}
      {renderFacebookDetail(service)}
      {renderXDetail(service)}
      {renderPhoneDetail(service)}
    </View>
  );

  return (
    <View style={styles.profileCompletionCard}>
      <View style={[styles.profileCompletionHeader, { flexDirection: rtl.rowDirection }]}>
        <Ionicons name="checkmark-circle-outline" size={20} color={colors.primary} />
        <Text style={[styles.profileCompletionTitle, { fontFamily: typo.fontFamily.medium }]}>{t('profile.completion.title')}</Text>
        <View style={[styles.profileCompletionProgress, { flexDirection: rtl.rowDirection }]}>
          <Text style={[styles.progressPercentage, { fontFamily: typo.fontFamily.bold }]}>{data.percentage}%</Text>
          <Text style={[styles.progressLabel, { fontFamily: typo.fontFamily.primary }]}>{t('profile.completion.percentComplete')}</Text>
          {onRefresh && (
            <TouchableOpacity
              style={styles.refreshButton}
              onPress={onRefresh}
              activeOpacity={0.7}
            >
              <Ionicons name="refresh" size={16} color={colors.primary} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      <View style={styles.progressBarContainer}>
        <View style={styles.progressBar}>
          <View style={[styles.progressFill, { width: `${data.percentage}%` }]} />
        </View>
      </View>

      {/* Business-wide stats — persistent, not tied to the carousel position */}
      {businessStats && (
        <View style={[styles.statsStrip, { flexDirection: rtl.rowDirection }]}>
          <StatChip icon="people-outline" value={businessStats.totalContacts} label={t('profile.stats.contacts')} />
          <StatChip icon="chatbubble-outline" value={businessStats.messagesThisMonth} label={t('profile.stats.messagesThisMonth')} />
          <StatChip icon="person-outline" value={businessStats.teamCount} label={t('profile.stats.teamMembers')} />
        </View>
      )}

      {/* All Services Slider */}
      {hasServices ? (
        <View style={styles.servicesSection}>
          <View style={[styles.servicesHeader, { flexDirection: rtl.rowDirection }]}>
            <Text style={[styles.servicesTitle, { fontFamily: typo.fontFamily.medium }]}>{t('profile.completion.allServices')}</Text>
            <View style={styles.serviceCounter}>
              <Text style={[styles.serviceCounterText, { fontFamily: typo.fontFamily.medium }]}>
                {currentServiceIndex + 1} / {allServices.length}
              </Text>
            </View>
          </View>

          {/* Current Service Card */}
          <View style={styles.serviceCardContainer} onLayout={handleServiceLayout}>
            {serviceCardWidth > 0 && (
              <ScrollView
                ref={serviceScrollRef}
                horizontal
                pagingEnabled
                showsHorizontalScrollIndicator={false}
                onMomentumScrollEnd={handleServiceScrollSettled}
                onScrollEndDrag={handleServiceScrollSettled}
                scrollEventThrottle={16}
              >
                {loopServices.map((service, index) => (
                  <View key={service.code + index} style={{ width: serviceCardWidth }}>
                    {renderServiceCard(service)}
                  </View>
                ))}
              </ScrollView>
            )}
          </View>

          {/* Navigation Controls */}
          <View style={[styles.navigationControls, { flexDirection: rtl.rowDirection }]}>
            <TouchableOpacity
              style={styles.navButton}
              onPress={goToPreviousService}
              activeOpacity={0.7}
            >
              <Ionicons
                name={rtl.mirrorIcon('chevron-back') as any}
                size={18}
                color={colors.primary}
              />
            </TouchableOpacity>

            {/* Service Indicators */}
            <View style={styles.serviceIndicators}>
              {allServices.map((_, index) => (
                <View
                  key={index}
                  style={[
                    styles.serviceIndicator,
                    index === currentServiceIndex && styles.serviceIndicatorActive,
                    // Color the dots based on service status
                    { backgroundColor: getStatusColor(allServices[index].status) }
                  ]}
                />
              ))}
            </View>

            <TouchableOpacity
              style={styles.navButton}
              onPress={goToNextService}
              activeOpacity={0.7}
            >
              <Ionicons
                name={rtl.mirrorIcon('chevron-forward') as any}
                size={18}
                color={colors.primary}
              />
            </TouchableOpacity>
          </View>
        </View>
      ) : (
        <View style={styles.noServices}>
          <Ionicons name="information-circle-outline" size={24} color={colors.border} />
          <Text style={[styles.noServicesText, { fontFamily: typo.fontFamily.medium }]}>{t('profile.completion.noServices')}</Text>
        </View>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  profileCompletionCard: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    padding: spacing.md,
    marginBottom: spacing.md,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 3,
    borderWidth: 1,
    borderColor: colors.border,
  },
  profileCompletionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  profileCompletionTitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    marginLeft: spacing.xs,
  },
  profileCompletionProgress: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginLeft: spacing.xs,
  },
  progressPercentage: {
    fontSize: typography.fontSize.lg,
    color: colors.primary,
    fontFamily: typography.fontFamily.bold,
    marginRight: spacing.xs,
  },
  progressLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  progressBarContainer: {
    marginBottom: spacing.sm,
  },
  progressBar: {
    height: 5,
    backgroundColor: colors.border,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: colors.primary,
    borderRadius: 3,
  },
  statsStrip: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  servicesSection: {
    marginBottom: spacing.sm,
  },
  servicesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  servicesTitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
  },
  serviceCounter: {
    backgroundColor: colors.primaryTransparent, // Tinted brand primary
    borderRadius: spacing.borderRadius.sm,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
  },
  serviceCounterText: {
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  serviceCardContainer: {
    width: '100%',
    marginBottom: spacing.sm,
  },
  serviceCard: {
    width: '100%',
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: spacing.borderRadius.lg,
    padding: spacing.sm,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 3,
    borderWidth: 1,
    borderColor: colors.border,
  },
  serviceHeader: {
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  serviceHeaderLeft: {
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    gap: spacing.xs,
  },
  serviceHeaderRight: {
    alignItems: 'center',
    gap: spacing.xs,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  serviceName: {
    flexShrink: 1,
    minWidth: 0,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  serviceDescription: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginBottom: spacing.xs,
    lineHeight: 16,
  },
  whatsappDetailRow: {
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  qualityDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  whatsappDetailText: {
    flexShrink: 1,
    minWidth: 0,
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  statusBadge: {
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: spacing.borderRadius.sm,
  },
  statusText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
  },
  settingsButton: {
    padding: spacing.xs,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: colors.background.secondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  completeServiceIconButton: {
    width: 28,
    height: 28,
    borderRadius: spacing.borderRadius.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navigationControls: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: spacing.xs,
  },
  navButton: {
    padding: spacing.xs,
  },
  serviceIndicators: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.xs,
  },
  serviceIndicator: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
  },
  serviceIndicatorActive: {
    backgroundColor: colors.primary,
  },
  noServices: {
    alignItems: 'center',
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  noServicesText: {
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    marginTop: spacing.xs,
  },
  completeProfileButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: spacing.borderRadius.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginTop: spacing.sm,
  },
  completeProfileButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
  refreshButton: {
    padding: spacing.xs,
  },
});
